/**
 * The docs site's own interface words (menu, buttons, table headings) — one home.
 *
 * The developer docs are English only, like the API they describe (design §0.5 writes their rules in
 * English; every page's text comes from the API and `content/**`), so these words are not
 * i18n keys: adding them to every app locale would translate the frame around an untranslated page.
 * Whether the docs ever get locales is an open question in the FE-1 report.
 */
export const COPY = {
  siteName: "Refabric Docs",
  search: "Search",
  searchShortcut: "⌘K",
  searchPlaceholder: "Search the docs",
  searchUnavailable: "Search is not available here.",
  searchNoResults: "No results.",
  contactSales: "Contact sales",
  signUp: "Sign up",
  navigation: "Navigation",
  closeNavigation: "Close navigation",
  status: "Status",
  support: "Support",
  panel: "Panel",
  theme: "Theme",
  themeSystem: "System",
  themeLight: "Light",
  themeDark: "Dark",
  onThisPage: "On this page",
  copyPage: "Copy page",
  copyPageDone: "Copied",
  viewAsMarkdown: "View as Markdown",
  openInChatGpt: "Open in ChatGPT",
  openInClaude: "Open in Claude",
  askAboutPage: "Read {url} and answer questions about it.",
  helpful: "Was this page helpful?",
  yes: "Yes",
  no: "No",
  thanks: "Thanks for the feedback.",
  previous: "Previous",
  next: "Next",
  terms: "Terms",
  privacy: "Privacy",
  copy: "Copy",
  copied: "Copied",
  copyLink: "Copy link to this field",
  loading: "Loading…",
  notFoundTitle: "Page not found",
  notFoundBody: "This page does not exist in the docs.",
  loadFailed: "The docs could not be loaded. Reload the page to try again.",
  required: "required",
  optional: "optional",
  nullable: "nullable",
  deprecated: "deprecated",
  defaultLabel: "Default",
  chooseWhen: "Choose when",
  values: "Values",
  example: "Example",
  showChildren: "Show child attributes",
  hideChildren: "Hide child attributes",
  authorization: "Authorization",
  keyHeader: "Authorization: Key $REFABRIC_API_KEY",
  scope: "Scope",
  noKey: "No key needed.",
  pathParameters: "Path parameters",
  queryParameters: "Query parameters",
  headerParameters: "Header parameters",
  parameters: "Parameters",
  body: "Body",
  response: "Response",
  request: "Request",
  operation: "Operation",
  availableOperations: "Available operations",
  morePages: "More",
  baseUrl: "Base URL",
  hubs: "Hubs",
  tasksByCategory: "Tasks by category",
  allTasks: "All tasks",
  gettingStarted: "Getting started",
  task: "Task",
  category: "Category",
  description: "Description",
  tasksCount: "{count} tasks",
  tasks: "Tasks",
  quickStart: "Quick start",
  examples: "Examples",
  inputs: "Inputs",
  outputs: "Outputs",
  inputSchema: "Input schema",
  outputSchema: "Output schema",
  requiredExample: "Required-fields example",
  fullExample: "Full example",
  responseExample: "Response example",
  resultExample: "Result example",
  pricing: "Pricing",
  pricingLine: "Estimate a request before you run it, or see the price of each option.",
  endpoint: "Endpoint",
  builtFor: "Built for",
  whatYouGet: "What you get",
  prompting: "Prompting",
  specs: "Specs",
  howItCompares: "Choosing a task",
  useThisWhen: "Use this when {text}",
  useOtherWhen: "Use {task} when {text}",
  errors: "Errors",
  related: "Related",
  limitations: "Good to know",
  changes: "Changes",
  forAgents: "For agents and code generation",
  qualityNote: "Schema changes follow Versioning.",
  curlWaitNote: "cURL waits for the result with Prefer: wait; when the job outlives the wait, it answers 202 with the job's URLs.",
  deprecatedTask: "This task is deprecated.",
  filterByTag: "Filter by tag",
  allTags: "All",
  compatibility: "Compatibility",
  readMore: "Read more",
  removal: "Removal",
  removalHowToIdentify: "How to identify",
  removalIfAlready: "If you already…",
  removalToKeepWorking: "To keep working",
  removalWhatStays: "What stays the same",
  usedIn: "Used in",
  value: "Value",
  rss: "RSS",
};

/** `{name}` placeholders → values (the same single-brace form the app's i18n uses). */
export const fill = (text: string, values: Record<string, string | number>): string => text.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match));
